import * as React from "react";
import { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { addToast, Card, CardBody, CardFooter, cn, Divider, Switch } from "@heroui/react";
import { Route, Routes } from "react-router-dom";
import { motion } from "framer-motion";
import {
  critiqueWords,
  getDocumentText,
  registerParagraphAddedEvent,
  registerParagraphChangedEvent,
} from "../../taskpane";
import { VocabularyPage } from "./Vocabulary";
import { GridBackground } from "./ui/GridBackground";
import { SpellCheckPage } from "./Spellcheck";
import { NavigationTabs } from "./NavigationTabs";
import { DictionaryPage } from "./Dictionary";
import { Corrections } from "../../../types";
import { useApi } from "../hooks/useApi";
import { useTranslation } from "react-i18next";

const DEBOUNCE_DELAY = 700;

const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => callback(...args), delay);
    },
    [callback, delay]
  );
};

export const Home: React.FC = () => {
  const [isAlwaysCheck, setIsAlwaysCheck] = useState<boolean>(true);
  const [corrections, setCorrections] = useState<Corrections[]>([]);
  const { loading, post, get } = useApi();
  const { t } = useTranslation();

  const isAlwaysCheckRef = useRef(isAlwaysCheck);
  const lastCheckedTextRef = useRef<string>("");
  const skippingWordsRef = useRef<string[]>([]);

  const debouncedHandleParagraphChange = useDebounce(
    (event: Word.ParagraphChangedEventArgs) => handleParagraphEvent(event, "changed"),
    DEBOUNCE_DELAY
  );

  const debouncedHandleParagraphAdded = useDebounce(
    (event: Word.ParagraphAddedEventArgs) => handleParagraphEvent(event, "added"),
    DEBOUNCE_DELAY
  );
  const handleParagraphEvent = useCallback(
    async (
      _: Word.ParagraphChangedEventArgs | Word.ParagraphAddedEventArgs,
      eventType: "changed" | "added"
    ) => {
      if (!isAlwaysCheckRef.current) return;

      try {
        await Word.run(async (_) => {
          const text = await getDocumentText();
          await fetchCorrections(text);
        });
      } catch (err) {
        console.error(`Error handling paragraph ${eventType}:`, err);
      }
    },
    []
  );

  const fetchSkippingWords = async (): Promise<void> => {
    const data = await get("/spell/skipping-words");
    skippingWordsRef.current = data;
  };

  const addSkippingWord = async (word: string): Promise<void> => {
    await post("/spell/skipping-words", { word });
    await fetchSkippingWords();
  };

  const fetchCorrections = useCallback(
    async (texts: string): Promise<void> => {
      if (!texts.trim()) return;

      const filteredWords = texts
        .split(/\s+/)
        .filter((word) => !skippingWordsRef.current.includes(word));

      if (filteredWords.length === 0) return;

      const filteredText = filteredWords.join(" ");

      const data = await post("/spell/check", { text: filteredText });
      setCorrections([]);
      if (data.corrections && Array.isArray(data.corrections)) {
        setCorrections(data.corrections);
        await critiqueWords(data.corrections);
        lastCheckedTextRef.current = texts;
      }
    },
    [skippingWordsRef, post]
  );

  useEffect(() => {
    if (corrections.length > 0) critiqueWords(corrections).catch(console.error);
  }, [corrections]);

  useEffect(() => {
    isAlwaysCheckRef.current = isAlwaysCheck;
  }, [isAlwaysCheck]);

  const startListeningWordEvents = useCallback(async () => {
    try {
      await registerParagraphChangedEvent(async (event) => debouncedHandleParagraphChange(event));
      await registerParagraphAddedEvent(async (event) => debouncedHandleParagraphAdded(event));
    } catch (err) {
      console.error("Error registering paragraph change event:", err);
    }
  }, [debouncedHandleParagraphChange, debouncedHandleParagraphAdded]);

  useEffect(() => {
    fetchSkippingWords().finally(() => {
      getDocumentText().then((text) => fetchCorrections(text).catch(console.error));
    });
    startListeningWordEvents().catch(console.error);
  }, []);

  const handleToggleAlwaysCheck = (value: boolean) => {
    setIsAlwaysCheck(value);
    isAlwaysCheckRef.current = value;
  };

  const handleRefetchCorrections = async () => {
    const text = await getDocumentText();
    await fetchCorrections(text);
  };

  return (
    <GridBackground>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 80, damping: 15 }}
      >
        <NavigationTabs />
      </motion.div>
      <Card shadow="md" className="border-none h-[85vh] shadow-xl rounded-2xl overflow-hidden">
        <CardBody className="flex w-full max-w-[95%] sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-xl 2xl:max-w-2xl h-full flex-col p-6">
          <Routes>
            <Route
              path="/"
              element={
                <SpellCheckPage
                  corrections={corrections}
                  loading={loading}
                  addSkippingWord={addSkippingWord}
                  refetchCorrections={handleRefetchCorrections}
                />
              }
            />
            <Route path="/vocabulary" element={<VocabularyPage />} />
            <Route path="/dictionary" element={<DictionaryPage />} />
          </Routes>
        </CardBody>

        <Divider />

        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.1 }}
          className="flex justify-center"
        >
          <CardFooter className="rounded-b-2xl flex justify-center">
            <Switch
              isSelected={isAlwaysCheck}
              onValueChange={handleToggleAlwaysCheck}
              color="success"
              classNames={{
                base: cn(
                  "inline-flex flex-row-reverse bg-[#f4f3eb] w-full max-w-md bg-content1 hover:bg-content2 items-center",
                  "justify-between cursor-pointer rounded-lg gap-2 p-4",
                  "border-2 border-transparent transition-all duration-300",
                  "hover:shadow-md hover:scale-[1.01]",
                  "data-[selected=true]:border-success"
                ),
                wrapper: "p-0 h-4 overflow-visible bg-[#f4f3eb] ",
                thumb: cn(
                  "w-6 h-6 border-2 shadow-lg",
                  "!border-transparent transition-all duration-300",
                  "group-data-[selected=true]:ms-6",
                  "group-data-[pressed=true]:w-7",
                  "group-data-pressed:group-data-selected:ms-4"
                ),
              }}
            >
              <div className="flex flex-col gap-1">
                <p className="text-medium font-semibold">{t("alwaysCheckSpelling")}</p>
                <p className="text-tiny text-default-400">{t("alwaysCheckSpellingDescription")}</p>
              </div>
            </Switch>
          </CardFooter>
        </motion.div>
      </Card>
    </GridBackground>
  );
};
